import React, { useMemo } from "react";
import {
  calculateOrderSettlement,
  reconcileOrderSettlement,
} from "../utils/orderSettlementCalculations";

const GOLD = "#8a6b32";
const INK = "#0f172a";
const MUTED = "#64748b";
const LINE = "#e2e8f0";

const r2 = (n) => Number(n || 0).toFixed(2);
const r3 = (n) => Number(n || 0).toFixed(3);
const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Per-order four-party reconciliation.
 * Everything is derived from the single settlement engine, so
 *   Buyer Total = Seller Settlement + Operator Margin + JMI Commission
 * must hold exactly (and is asserted, not assumed).
 */
export const buildReconciliation = (order = {}, rtgsSaleRate = 0) => {
  const s = calculateOrderSettlement(order);
  const check = reconcileOrderSettlement(order);
  const rate = Number(rtgsSaleRate) || 0;

  const jmiFineValue = s.jmiFine * rate;
  const jmiCommission = s.totalAmountJmi + jmiFineValue;
  const jmiCleared = Boolean(order.jmiSettled || order.jmiCommissionSettled);

  return {
    id: order.docId || order.id,
    productName: order.productName || order.product || "—",
    buyerId: order.buyerId || "",
    sellerId: order.sellerId || "",
    orderStatus: order.orderStatus || "",

    sets: s.TotalSet || 0,
    grossWt: s.TotalgrossWt || 0,
    netWt: s.TotalnetWt || 0,
    fineWeight: s.fineWeight || 0,

    buyerTotal: s.totalAmount || 0,
    sellerAmount: s.totalAmountSeller || 0,
    operatorAmount: s.totalAmountOperator || 0,
    jmiCommission,
    jmiFineValue,
    jmiMc: s.totalAmountJmi || 0,

    sellerFine: s.sellerFine || 0,
    operatorFine: s.operatorFine || 0,
    jmiFine: s.jmiFine || 0,

    rate,
    jmiCleared,
    jmiOutstanding: jmiCleared ? 0 : jmiCommission,

    isBalanced: check.isBalanced,
    amountDelta: check.amountDelta,
    fineDelta: check.fineDelta,
  };
};

export const summarise = (rows = []) =>
  rows.reduce(
    (acc, r) => ({
      orders: acc.orders + 1,
      sets: acc.sets + (r.sets || 0),
      grossWt: acc.grossWt + (r.grossWt || 0),
      netWt: acc.netWt + (r.netWt || 0),
      fineWeight: acc.fineWeight + (r.fineWeight || 0),
      buyerTotal: acc.buyerTotal + (r.buyerTotal || 0),
      sellerAmount: acc.sellerAmount + (r.sellerAmount || 0),
      operatorAmount: acc.operatorAmount + (r.operatorAmount || 0),
      jmiCommission: acc.jmiCommission + (r.jmiCommission || 0),
      jmiOutstanding: acc.jmiOutstanding + (r.jmiOutstanding || 0),
      unbalanced: acc.unbalanced + (r.isBalanced ? 0 : 1),
      jmiClearedCount: acc.jmiClearedCount + (r.jmiCleared ? 1 : 0),
    }),
    {
      orders: 0,
      sets: 0,
      grossWt: 0,
      netWt: 0,
      fineWeight: 0,
      buyerTotal: 0,
      sellerAmount: 0,
      operatorAmount: 0,
      jmiCommission: 0,
      jmiOutstanding: 0,
      unbalanced: 0,
      jmiClearedCount: 0,
    },
  );

const Stat = ({ label, value, sub, tone = INK }) => (
  <div
    style={{
      flex: "1 1 150px",
      minWidth: 0,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderLeft: `3px solid ${tone}`,
      borderRadius: 10,
      padding: "10px 12px",
    }}
  >
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: MUTED,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 19,
        fontWeight: 800,
        color: tone,
        fontVariantNumeric: "tabular-nums",
        marginTop: 2,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: 10, color: MUTED, whiteSpace: "nowrap" }}>{sub}</div>
    )}
  </div>
);

/** KPI row — Buyer / Seller / Operator / JMI split. */
export const SettlementKpis = ({ rows = [], rate = 0 }) => {
  const t = useMemo(() => summarise(rows), [rows]);
  const balanced = t.unbalanced === 0;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
      <Stat
        label="Orders"
        value={t.orders}
        sub={`${t.sets} sets · ${r3(t.netWt)}g net`}
      />
      <Stat
        label="Buyer Total"
        value={inr(t.buyerTotal)}
        sub="what the buyer owes"
        tone={GOLD}
      />
      <Stat
        label="Seller Settlement"
        value={inr(t.sellerAmount)}
        sub="payable to the seller"
        tone="#0f766e"
      />
      <Stat
        label="Operator Margin"
        value={inr(t.operatorAmount)}
        sub="operator profit"
        tone="#b45309"
      />
      <Stat
        label="JMI Commission"
        value={inr(t.jmiCommission)}
        sub={`fine @ ${r2(rate)}/g`}
        tone="#6d28d9"
      />
      <Stat
        label="JMI Outstanding"
        value={inr(t.jmiOutstanding)}
        sub={`${t.jmiClearedCount}/${t.orders} cleared`}
        tone={t.jmiOutstanding > 0 ? "#b91c1c" : "#15803d"}
      />
      <Stat
        label="Reconciliation"
        value={balanced ? "Balanced" : `${t.unbalanced} off`}
        sub={
          balanced
            ? "buyer = seller + operator + JMI"
            : "investigate the flagged orders"
        }
        tone={balanced ? "#15803d" : "#b91c1c"}
      />
    </div>
  );
};

const Th = ({ children, align = "right" }) => (
  <th
    style={{
      padding: "8px 10px",
      textAlign: align,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "#ffffff",
      background: GOLD,
      borderBottom: "2px solid #6f5527",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 1,
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = "right", tone }) => (
  <td
    style={{
      padding: "8px 10px",
      fontSize: 12,
      textAlign: align,
      borderBottom: "1px solid #eef2f7",
      fontVariantNumeric: "tabular-nums",
      color: tone || INK,
      fontWeight: tone ? 700 : 500,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </td>
);

const StatusChip = ({ row }) =>
  row.jmiCleared ? (
    <span
      style={{
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
        borderRadius: 999,
        padding: "2px 8px",
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      JMI Cleared
    </span>
  ) : (
    <span
      style={{
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fcd34d",
        borderRadius: 999,
        padding: "2px 8px",
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      JMI Due {inr(row.jmiOutstanding)}
    </span>
  );

/**
 * Full settlement table: what the buyer paid, what the seller gets, the
 * operator margin and the JMI commission — per order — with a live
 * reconciliation check and the JMI cleared/pending state.
 */
export const SettlementTable = ({ rows = [] }) => (
  <div
    style={{
      border: `1px solid ${LINE}`,
      borderRadius: 12,
      overflow: "auto",
      maxHeight: "60vh",
      background: "#fff",
      boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    }}
  >
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 980 }}>
      <thead>
        <tr>
          <Th align="left">Product</Th>
          <Th>Sets</Th>
          <Th>Net (g)</Th>
          <Th>Fine (g)</Th>
          <Th>Buyer Paid</Th>
          <Th>Seller Paid</Th>
          <Th>Operator</Th>
          <Th>JMI</Th>
          <Th align="center">JMI Status</Th>
          <Th align="center">Check</Th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <Td align="left" >No orders in this settlement set.</Td>
          </tr>
        )}
        {rows.map((row, i) => (
          <tr
            key={row.id || i}
            style={{ background: i % 2 ? "#fbfcfe" : "#ffffff" }}
          >
            <Td align="left">
              <div style={{ fontWeight: 700 }}>{row.productName}</div>
              <div style={{ fontSize: 10, color: MUTED, fontFamily: "monospace" }}>
                {row.sellerId || row.buyerId || row.id}
              </div>
            </Td>
            <Td>{r3(row.sets)}</Td>
            <Td>{r3(row.netWt)}</Td>
            <Td>{r3(row.fineWeight)}</Td>
            <Td tone={GOLD}>{inr(row.buyerTotal)}</Td>
            <Td tone="#0f766e">{inr(row.sellerAmount)}</Td>
            <Td tone="#b45309">{inr(row.operatorAmount)}</Td>
            <Td tone="#6d28d9">{inr(row.jmiCommission)}</Td>
            <Td align="center">
              <StatusChip row={row} />
            </Td>
            <Td align="center">
              <span
                style={{
                  background: row.isBalanced ? "#dcfce7" : "#fee2e2",
                  color: row.isBalanced ? "#166534" : "#991b1b",
                  borderRadius: 999,
                  padding: "2px 8px",
                  fontSize: 10.5,
                  fontWeight: 700,
                }}
                title={
                  row.isBalanced
                    ? "Buyer total = seller + operator + JMI"
                    : `amount delta ${r2(row.amountDelta)} · fine delta ${r3(row.fineDelta)}`
                }
              >
                {row.isBalanced ? "OK" : `Δ ${r2(row.amountDelta)}`}
              </span>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SettlementKpis;
